import '../App.css';
import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import * as Icon from 'react-feather';
import MainStructure from '../components/general/MainStructure';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import linkedinImg  from '../assets/linkedin.png';
import instagramImg from '../assets/instagram.png';
import twitterImg   from '../assets/twitter.png';
import facebookImg  from '../assets/facebook.png';
import tiktokImg    from '../assets/tiktok.png';
import threadsImg   from '../assets/threads.png';
import blueskyImg   from '../assets/bluesky.png';
import discordImg   from '../assets/discord.png';
import pinterestImg from '../assets/pinterest.png';
import youtubeImg   from '../assets/youtube.png';

// ── Constants ─────────────────────────────────────────────────────────────────

const RANGES = [
  { label: '7D',  days: 7  },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '12M', days: 365 },
];

const PLATFORM_META = {
  linkedin:      { label: 'LinkedIn',       color: '#0077b5', icon: linkedinImg  },
  linkedin_page: { label: 'LinkedIn Page',  color: '#004182', icon: linkedinImg  },
  instagram:     { label: 'Instagram',      color: '#e1306c', icon: instagramImg },
  twitter:       { label: 'X / Twitter',    color: '#000000', icon: twitterImg   },
  youtube:       { label: 'YouTube',        color: '#ff0000', icon: youtubeImg   },
  facebook:      { label: 'Facebook',       color: '#1877f2', icon: facebookImg  },
  tiktok:        { label: 'TikTok',         color: '#010101', icon: tiktokImg    },
  threads:       { label: 'Threads',        color: '#000000', icon: threadsImg   },
  bluesky:       { label: 'Bluesky',        color: '#0085ff', icon: blueskyImg   },
  discord:       { label: 'Discord',        color: '#5865F2', icon: discordImg   },
  pinterest:     { label: 'Pinterest',      color: '#e60023', icon: pinterestImg },
};

const CHART_LINES = [
  { key: 'impressions', color: '#4f46e5' },
  { key: 'likes',       color: '#ef4444' },
  { key: 'comments',    color: '#8b5cf6' },
  { key: 'shares',      color: '#10b981' },
  { key: 'clicks',      color: '#f59e0b' },
];

const KPI_DEFS = [
  { key: 'impressions', label: 'Impressions', icon: Icon.Eye,           color: '#4f46e5' },
  { key: 'reach',       label: 'Reach',       icon: Icon.Users,         color: '#0ea5e9' },
  { key: 'clicks',      label: 'Clicks',      icon: Icon.MousePointer,  color: '#f59e0b' },
  { key: 'likes',       label: 'Likes',       icon: Icon.Heart,         color: '#ef4444' },
  { key: 'comments',    label: 'Comments',    icon: Icon.MessageSquare, color: '#8b5cf6' },
  { key: 'shares',      label: 'Shares',      icon: Icon.Share2,        color: '#10b981' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function offsetDate(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function fmt(n) {
  if (n == null) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000)    return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KPICard({ def, value, loading }) {
  const { label, icon: Ico, color } = def;
  return (
    <div className="col-6 col-sm-4 col-md-2 mb-3">
      <div style={{ background: '#fff', border: '1px solid #b0bec5', borderRadius: 12, padding: '14px 12px' }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#7d8fa3', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {label}
          </span>
          <Ico size={13} color={color} />
        </div>
        <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f172a' }}>
          {loading ? <span style={{ color: '#e2e8f0' }}>—</span> : fmt(value)}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ background: '#f8fafc', border: '1px dashed #d1d9e0', borderRadius: 12, padding: '48px 24px', textAlign: 'center' }}>
      <Icon.BarChart2 size={36} color="#d1d9e0" style={{ marginBottom: 12 }} />
      <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
        No analytics data yet for this period.<br />
        Analytics sync runs every 6 hours after posts are published.
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function Analytics() {
  const [rangeDays,   setRangeDays]   = useState(30);
  const [platform,    setPlatform]    = useState('all');
  const [granularity, setGranularity] = useState('day');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date:  offsetDate(rangeDays),
        end_date:    new Date().toISOString().split('T')[0],
        granularity: rangeDays > 90 ? 'month' : granularity,
      });
      if (platform !== 'all') params.set('platform', platform);

      const res = await fetch(
        `https://api.schedpilot.com/wp-json/api/v1/analytics-overview?${params}`,
        { credentials: 'include' }
      );
      const json = await res.json();
      if (json.code === 200) {
        setData(json);
      } else {
        setError(json.message || 'Failed to load analytics.');
      }
    } catch {
      setError('Could not connect to the server.');
    } finally {
      setLoading(false);
    }
  }, [rangeDays, platform, granularity]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const totals      = data?.totals      || {};
  const trend       = data?.trend       || [];
  const byPlatform  = data?.by_platform || {};
  const hasTrend    = trend.length > 0;
  const platforms   = Object.keys(byPlatform);

  // Compute effective granularity label for x-axis
  const effectiveGran = rangeDays > 90 ? 'month' : granularity;

  const s = {
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 600,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7d8fa3',
      marginBottom: 12,
    },
  };

  return (
    <MainStructure activeItem="analytics">
      <div className="container-fluid px-0">

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
          <div>
            <h1 style={{ fontSize: '1.3rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>Analytics</h1>
            <p style={{ fontSize: '0.85rem', color: '#7d8fa3', margin: 0 }}>
              Performance insights across your connected platforms
            </p>
          </div>

          {/* Date range pills */}
          <div className="d-flex gap-2 flex-wrap">
            {RANGES.map(r => (
              <button
                key={r.days}
                onClick={() => setRangeDays(r.days)}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                  border: '1px solid',
                  borderColor:     rangeDays === r.days ? '#4f46e5' : '#e2e8f0',
                  background:      rangeDays === r.days ? '#4f46e5' : '#fff',
                  color:           rangeDays === r.days ? '#fff'    : '#64748b',
                  cursor: 'pointer',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Platform filter tabs ────────────────────────────── */}
        <div className="d-flex gap-2 flex-wrap mb-4">
          {['all', ...Object.keys(PLATFORM_META)].map(p => {
            const meta  = PLATFORM_META[p];
            const label = p === 'all' ? 'All Platforms' : meta?.label || p;
            const active = platform === p;
            return (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                style={{
                  padding: '4px 12px', borderRadius: 8, fontSize: '0.78rem',
                  border: '1px solid',
                  borderColor: active ? (meta?.color || '#4f46e5') : '#e2e8f0',
                  background:  active ? (meta?.color || '#4f46e5') : '#fff',
                  color:       active ? '#fff' : '#64748b',
                  cursor: 'pointer',
                  fontWeight: active ? 600 : 400,
                }}
              >
                <span className="d-flex align-items-center gap-1">
                  {meta?.icon && (
                    <img
                      src={meta.icon}
                      alt=""
                      style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        background: '#fff', padding: 1,
                      }}
                    />
                  )}
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="alert alert-danger mb-4" style={{ borderRadius: 10, fontSize: '0.875rem' }}>{error}</div>
        )}

        {/* ── KPI cards ──────────────────────────────────────── */}
        <div className="row mb-4">
          {KPI_DEFS.map(def => (
            <KPICard key={def.key} def={def} value={totals[def.key]} loading={loading} />
          ))}
        </div>

        {/* ── Trend chart ────────────────────────────────────── */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px', marginBottom: 20 }}>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <p style={s.sectionLabel}>Trend</p>
            {rangeDays <= 90 && (
              <div className="d-flex gap-2">
                {['day', 'month'].map(g => (
                  <button
                    key={g}
                    onClick={() => setGranularity(g)}
                    style={{
                      padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem',
                      border: '1px solid',
                      borderColor: granularity === g ? '#4f46e5' : '#e2e8f0',
                      background:  granularity === g ? '#eff6ff' : '#fff',
                      color:       granularity === g ? '#4f46e5' : '#94a3b8',
                      cursor: 'pointer',
                    }}
                  >
                    {g === 'day' ? 'Daily' : 'Monthly'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d9e0' }}>
              <Icon.Loader size={24} />
            </div>
          ) : !hasTrend ? (
            <EmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend} margin={{ top: 4, right: 12, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  tickFormatter={v => effectiveGran === 'month' ? v : v.slice(5)}
                />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={fmt} />
                <Tooltip
                  contentStyle={{ fontSize: '0.78rem', borderRadius: 8, border: '1px solid #e2e8f0' }}
                  formatter={(val, name) => [fmt(val), name.charAt(0).toUpperCase() + name.slice(1)]}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingTop: 8 }} />
                {CHART_LINES.map(l => (
                  <Line
                    key={l.key}
                    type="monotone"
                    dataKey={l.key}
                    stroke={l.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Platform breakdown table ────────────────────────── */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <p style={{ ...s.sectionLabel, marginBottom: 0 }}>Breakdown by Platform</p>
          </div>

          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#d1d9e0' }}>
              <Icon.Loader size={20} />
            </div>
          ) : platforms.length === 0 ? (
            <div style={{ padding: '32px 20px' }}>
              <EmptyState />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '10px 20px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}>Platform</th>
                    {['Impressions','Reach','Clicks','Likes','Comments','Shares'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none', textAlign: 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {platforms.map((p, i) => {
                    const row  = byPlatform[p];
                    const meta = PLATFORM_META[p] || { label: p, color: '#64748b' };
                    return (
                      <tr key={p} style={{ borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                        <td style={{ padding: '12px 20px' }}>
                          <div className="d-flex align-items-center gap-2">
                            {meta.icon
                              ? <img src={meta.icon} alt={meta.label} style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0 }} />
                              : <div style={{ width: 18, height: 18, borderRadius: 4, background: meta.color, flexShrink: 0 }} />
                            }
                            <span style={{ fontWeight: 500, color: '#334155' }}>{meta.label}</span>
                          </div>
                        </td>
                        {['impressions','reach','clicks','likes','comments','shares'].map(k => (
                          <td key={k} style={{ padding: '12px', textAlign: 'right', color: '#475569', fontWeight: 500 }}>
                            {fmt(row[k] || 0)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                    <td style={{ padding: '12px 20px', fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>Total</td>
                    {['impressions','reach','clicks','likes','comments','shares'].map(k => (
                      <td key={k} style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                        {fmt(totals[k] || 0)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </MainStructure>
  );
}

export default Analytics;
